// src/contexts/PageContext.tsx

import React, { createContext, useState, useEffect, useCallback, useContext, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { getContentLibrary, updateQuickAccess } from '@/services/templateService';
import { getSubscription } from '@/services/userService';
import { ISubscription } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useProjects } from '@/components/create-project/hooks/useProjects';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';

interface Template {
  templateId: string;
  templateName: string;
  pageKey: string;
  isSinglePage: boolean;
  quickAccess: boolean;
}

interface PageContextType {
  currentPage: string;
  setCurrentPage: (page: string) => void;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  templates: Template[];
  toggleQuickAccess: (pageKey: string) => Promise<void>;
  showHeader: boolean;
  setShowHeader: (show: boolean) => void;
  refreshTemplates: () => Promise<void>;
  subscription: ISubscription | null;
  isPageAccessible: (path: string) => boolean;
}

const PageContext = createContext<PageContextType | undefined>(undefined);

export const PageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentPage, setCurrentPage] = useState('Dashboard');
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? saved === 'true' : false;
  });
  const [templates, setTemplates] = useState<Template[]>([]);
  const [subscription, setSubscription] = useState<ISubscription | null>(null);
  const [showHeader, setShowHeader] = useState(false);
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const { selectedProject } = useProjects();
  const navigate = useNavigate();

  // List of paths that are restricted to projects
  const restrictedPaths = [
    '/content-types',
    '/template-builder', 
    '/asset-manager',
    '/content-library',
    '/attribute-sets'
  ];
  // List pages where header should be shown
  const pagesWithHeader = [
    '/', 
    '/template-builder',
    '/content-types', 
    '/pages', 
    '/settings', 
    '/profile',
    '/asset-manager',
    '/attribute-sets',
    '/content-library'
  ];

  const isPageAccessible = useCallback((path: string) => {
    if (restrictedPaths.some(p => path.startsWith(p))) {
      return Boolean(selectedProject?.projectId);
    }
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.projectId]);

  useEffect(() => {
    const currentPath = location.pathname;
    if (!isPageAccessible(currentPath)) {
      toast.error('Please select a project first');
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, isPageAccessible]);


  // Fetch templates and update state
  const fetchTemplates = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    try {
      const res = await getContentLibrary();
      if (res.success) {
        setTemplates(res.data);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  }, [isAuthenticated]);

  const fetchSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    if (subscription) {
      return;
    }
    try {
      const res = await getSubscription();
      if (res.success) {
        setSubscription(res.data);
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const refreshTemplates = useCallback(async () => {
    try {
      await fetchTemplates();
    } catch (error) {
      console.error('Error refreshing templates:', error);
    }
  }, [fetchTemplates]);

  // Initial fetch on mount and auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchTemplates();
      fetchSubscription();
    } else {
      setTemplates([]);
      setSubscription(null);
    }
  }, [isAuthenticated, fetchTemplates, fetchSubscription]);

  const toggleQuickAccess = async (pageKey: string) => {
    try {
      const res = await updateQuickAccess(pageKey);
      if (res.success) {
        toast.success(res.data);
        // Refresh the entire template list instead of updating locally
        await refreshTemplates();
      }
    } catch (error) {
      console.error('Error toggling quick access:', error);
      toast.error('Failed to update quick access');
    }
  };

  // Update current page and header visibility based on route
  useEffect(() => {
    const routeToPageName: { [key: string]: string } = {
      '/': 'Dashboard',
      '/template-builder': 'Template Builder',
      '/content-types': 'Content Types',
      '/pages': 'Pages',
      '/settings': 'Settings',
      '/profile': 'Profile Settings',
      '/support': 'Support Dashboard',
      '/asset-manager': 'Asset Manager',
      '/attribute-sets': 'Attribute Sets',
      '/content-library': 'Content Library',
    };
    const rootPath = '/' + location.pathname.split('/')[1];
    const pageName = routeToPageName[rootPath] || 'Unknown Page';

    // Set header visibility based on predefined pages
    setShowHeader(pagesWithHeader.includes(rootPath));
    setCurrentPage(pageName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return (
    <PageContext.Provider
      value={{
        currentPage,
        setCurrentPage,
        collapsed,
        setCollapsed,
        templates,
        toggleQuickAccess,
        showHeader,
        setShowHeader,
        refreshTemplates,
        subscription,
        isPageAccessible
      }}
    >
      <main className="flex flex-col sm:flex-row h-full">
        <Sidebar />
        <section className={`transition-all duration-200 w-full flex-1 pr-10 pb-4 sm:pb-6 xl:pb-10 ${collapsed ? 'pl-[104px]' : 'pl-[296px]'}`}>
        {children}
      </section>
      </main>
    </PageContext.Provider>
  );
};

export const usePageContext = () => {
  const context = useContext(PageContext);
  if (context === undefined) {
    throw new Error('usePageContext must be used within a PageProvider');
  }
  return context;
};
